import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

import {
  planeColours,
  ROOM_PLAIN_GROUP_ID,
  ROOM_VOLUME_GROUP_ID,
  type Cornerstone,
} from "@/lib/viewer/cornerstone";
import {
  NAVIGATE,
  orientationLetters,
  planesFor,
  screenAxes,
  type OrientationLetters,
  type RoomLayout,
  type RoomPlane,
  type RoomTool,
  type RoomView,
} from "@/lib/viewer/room";
import { rangeOf, widthCentreOf } from "@/lib/viewer/windows";
import { RoomCell } from "./RoomCell";
import type { RoomEngine, ViewportHandle } from "./RoomViewport";

/** What a plane reports upward. */
export interface PlaneState {
  ready: boolean;
  index: number;
  total: number;
  voi: { width: number; centre: number } | null;
  letters: OrientationLetters | null;
  inverted: boolean;
}

const EMPTY_PLANE: PlaneState = { ready: false, index: 0, total: 0, voi: null, letters: null, inverted: false };

type VolVp = {
  element: HTMLDivElement;
  render: () => void;
  getProperties: () => { voiRange?: { lower: number; upper: number }; invert?: boolean };
  setProperties: (p: unknown) => void;
  resetProperties: () => void;
  resetCamera: () => void;
  getCamera: () => Record<string, unknown> & { focalPoint?: number[] };
  worldToCanvas: (p: [number, number, number]) => [number, number];
  getSliceIndex: () => number;
  getNumberOfSlices: () => number;
  setBlendMode: (mode: unknown, filter?: unknown[], immediate?: boolean) => void;
  setSlabThickness: (mm: number) => void;
  resetSlabThickness: () => void;
};

/**
 * Every volume the room has built, so it can let them go. A 400-slice CT
 * is about 200 MB of Float32 in the cache and the room is closed and
 * reopened case after case; MprStage's own note on this is the reason.
 */
const builtVolumes = new Set<string>();

export function releaseRoomVolumes(cs: Cornerstone): void {
  for (const id of builtVolumes) {
    try {
      cs.core.cache.removeVolumeLoadObject(id);
    } catch {
      /* never finished loading */
    }
  }
  builtVolumes.clear();
}

let seq = 0;

/**
 * The volume views -- MPR, MIP and MinIP -- drawn INSIDE the room's grid.
 *
 * ONLY EVER REACHED THROUGH `mprVerdict`, exactly as MprStage is: a
 * reformat across a gap does not fail, it interpolates and draws an
 * anatomy the patient does not have.
 *
 * One volume per series, shared by every plane and kept across layout
 * changes (the caller keys this component on series AND layout, and the
 * volume id on the series alone), so going from 2 x 1 to 2 x 2 does not
 * reload four hundred slices.
 *
 * Measurement stays on the stack. The crosshair owns the left button
 * while reference lines are on, and an annotation drawn on a reformat
 * has no source image to belong to -- the panel could not say where it
 * is, nor take the reader back to it.
 */
export const RoomVolume = forwardRef<
  ViewportHandle,
  {
    volumeKey: string;
    imageIds: string[];
    modality: string | null;
    layout: RoomLayout;
    view: Exclude<RoomView, "stack">;
    slab: number;
    refLines: boolean;
    navTool: RoomTool;
    overlays: boolean;
    title: string;
    titleLines: string[];
    windowName: (voi: { width: number; centre: number } | null) => string | null;
    /** The room's VOLUME engine, kept for the room's whole life. */
    getEngine: () => Promise<RoomEngine>;
    onState: (state: PlaneState) => void;
    onUnavailable: (reason: string) => void;
  }
>(function RoomVolume(
  {
    volumeKey,
    imageIds,
    modality,
    layout,
    view,
    slab,
    refLines,
    navTool,
    overlays,
    title,
    titleLines,
    windowName,
    getEngine,
    onState,
    onUnavailable,
  },
  ref,
) {
  const planes = planesFor(layout, modality);
  const hosts = useRef<(HTMLDivElement | null)[]>([]);
  const csRef = useRef<Cornerstone | null>(null);
  const engineRef = useRef<RoomEngine["engine"] | null>(null);
  const idsRef = useRef<string[]>([]);
  const [ready, setReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const [states, setStates] = useState<PlaneState[]>(() => planes.map(() => EMPTY_PLANE));
  const [active, setActive] = useState(0);

  const patchPlane = useCallback((i: number, p: Partial<PlaneState>) => {
    setStates((prev) => prev.map((s, j) => (j === i ? { ...s, ...p } : s)));
  }, []);

  useEffect(() => {
    onState(states[0] ?? EMPTY_PLANE);
  }, [states, onState]);

  const vp = useCallback((i: number) => {
    const id = idsRef.current[i];
    return id ? (engineRef.current?.getViewport(id) as VolVp | undefined) : undefined;
  }, []);

  const eachPlane = useCallback(
    (fn: (v: VolVp, plane: RoomPlane, i: number) => void) => {
      planes.forEach((plane, i) => {
        const v = plane ? vp(i) : undefined;
        if (plane && v) fn(v, plane, i);
      });
      engineRef.current?.renderViewports(idsRef.current.filter(Boolean));
    },
    // `planes` is fixed for the life of this mount (keyed on layout).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vp],
  );

  // ------------------------------------------------------------------
  // Mount: viewports, volume, groups.
  // ------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const n = ++seq;
    const volumeId = `cornerstoneStreamingImageVolume:rr-vol-${volumeKey}`;
    const ids = planes.map((p, i) => (p ? `rr-vol-${n}-${i}` : ""));
    idsRef.current = ids;
    // The crosshair needs two planes that CROSS. A lone plane has nothing
    // to cross; the fourth cell's second axial is parallel to the first.
    const orthogonal = planes.filter((p) => p && p.key !== "axial-second").length;
    const groupFor = (p: RoomPlane) =>
      orthogonal >= 2 && p.key !== "axial-second" ? ROOM_VOLUME_GROUP_ID : ROOM_PLAIN_GROUP_ID;

    // ONE ENGINE FOR EVERY VOLUME VIEW THE ROOM SHOWS, owned by the room,
    // exactly as the stack cells share one. An engine per mount gave every
    // layout change a new WebGL context while the cached volume's texture
    // still belonged to the old one -- "bindTexture: object does not
    // belong to this context" -- and each new context brought the page
    // nearer the browser's limit, past which it kills the oldest.
    let enabled: string[] = [];
    let engineId = "";

    (async () => {
      let room: RoomEngine;
      try {
        room = await getEngine();
      } catch (err) {
        if (!cancelled) onUnavailable(err instanceof Error ? err.message : "The viewer could not start.");
        return;
      }
      if (cancelled) return;
      const { cs, engine } = room;
      csRef.current = cs;
      engineRef.current = engine;
      engineId = engine.id;
      const { OrientationAxis, ViewportType } = cs.core.Enums;
      const axis = {
        axial: OrientationAxis.AXIAL,
        "axial-second": OrientationAxis.AXIAL,
        coronal: OrientationAxis.CORONAL,
        sagittal: OrientationAxis.SAGITTAL,
      } as const;
      planes.forEach((p, i) => {
        const element = hosts.current[i];
        const viewportId = ids[i];
        if (!p || !element || !viewportId) return;
        engine.enableElement({
          viewportId,
          type: ViewportType.ORTHOGRAPHIC,
          element,
          defaultOptions: { orientation: axis[p.key], background: [0, 0, 0] },
        });
        enabled.push(viewportId);
      });
      planes.forEach((p, i) => {
        if (!p || !ids[i]) return;
        planeColours.set(ids[i], p.colour);
        // Viewports BEFORE the volume and before the crosshair is made
        // active: Crosshairs builds its lines from the viewports in its
        // group at the moment it is activated (MprStage's finding).
        cs.tools.ToolGroupManager.getToolGroup(groupFor(p))?.addViewport(ids[i], engineId);
      });

      try {
        if (!cs.core.cache.getVolume(volumeId)) {
          const volume = (await cs.core.volumeLoader.createAndCacheVolume(volumeId, { imageIds })) as {
            load: (cb?: (e: unknown) => void) => void;
          };
          builtVolumes.add(volumeId);
          let loaded = 0;
          volume.load(() => {
            loaded += 1;
            if (!cancelled) setProgress(Math.min(1, loaded / imageIds.length));
          });
        } else {
          setProgress(1);
        }
      } catch {
        if (!cancelled) onUnavailable("This series could not be assembled into a volume.");
        return;
      }
      if (cancelled) return;

      const live = ids.filter(Boolean);
      await cs.core.setVolumesForViewports(engine as never, [{ volumeId }], live);
      if (cancelled) return;
      if (cancelled) return;
      planes.forEach((p, i) => {
        if (p?.window) vp(i)?.setProperties({ voiRange: rangeOf(p.window) });
      });
      engine.renderViewports(live);
      setReady(true);
    })();

    return () => {
      cancelled = true;
      const cs = csRef.current;
      planes.forEach((p, i) => {
        const id = ids[i];
        if (!p || !id) return;
        planeColours.delete(id);
        try {
          cs?.tools.ToolGroupManager.getToolGroup(groupFor(p))?.removeViewports(engineId, id);
        } catch {
          /* the group may already be gone */
        }
      });
      for (const id of enabled) {
        try {
          engineRef.current?.disableElement(id);
        } catch {
          /* the room's engine was destroyed first */
        }
      }
      enabled = [];
      engineRef.current = null;
    };
    // Keyed on series and layout by the caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  // The left button: the crosshair while reference lines are on and
  // there is something to cross; otherwise the reader's navigate tool.
  // ------------------------------------------------------------------
  useEffect(() => {
    const cs = csRef.current;
    if (!cs || !ready) return;
    const { MouseBindings } = cs.tools.Enums;
    const primary = { bindings: [{ mouseButton: MouseBindings.Primary }] };
    const nav = NAVIGATE.find((t) => t.id === navTool)?.cs ?? "Pan";
    for (const id of [ROOM_VOLUME_GROUP_ID, ROOM_PLAIN_GROUP_ID]) {
      const group = cs.tools.ToolGroupManager.getToolGroup(id);
      if (!group) continue;
      for (const t of NAVIGATE) group.setToolPassive(t.cs);
      const crosshair = id === ROOM_VOLUME_GROUP_ID && refLines;
      if (id === ROOM_VOLUME_GROUP_ID) {
        if (crosshair) group.setToolActive(cs.tools.CrosshairsTool.toolName, primary);
        else group.setToolDisabled(cs.tools.CrosshairsTool.toolName);
      }
      if (!crosshair) group.setToolActive(nav, primary);
    }
    engineRef.current?.renderViewports(idsRef.current.filter(Boolean));
  }, [ready, refLines, navTool]);

  // ------------------------------------------------------------------
  // Projection and slab. MPR is a thin composite; MIP and MinIP are the
  // brightest and darkest voxel through the slab.
  // ------------------------------------------------------------------
  useEffect(() => {
    const cs = csRef.current;
    if (!cs || !ready) return;
    const { BlendModes } = cs.core.Enums;
    eachPlane((v) => {
      if (view === "mpr") {
        v.setBlendMode(BlendModes.COMPOSITE);
        v.resetSlabThickness();
      } else {
        v.setBlendMode(view === "mip" ? BlendModes.MAXIMUM_INTENSITY_BLEND : BlendModes.MINIMUM_INTENSITY_BLEND);
        v.setSlabThickness(slab);
      }
    });
  }, [ready, view, slab, eachPlane]);

  // ------------------------------------------------------------------
  // Readouts per plane, and the fourth cell following the axial.
  // ------------------------------------------------------------------
  useEffect(() => {
    const cs = csRef.current;
    if (!cs || !ready) return;
    const { Events } = cs.core.Enums;
    const cleanups: (() => void)[] = [];
    const second = planes.findIndex((p) => p?.key === "axial-second");

    planes.forEach((p, i) => {
      const v = p ? vp(i) : undefined;
      if (!p || !v) return;
      const element = v.element;
      const readVoi = () => {
        const range = v.getProperties().voiRange;
        if (range) patchPlane(i, { voi: widthCentreOf(range) });
      };
      const readCamera = () => {
        const axes = screenAxes(v);
        patchPlane(i, {
          ready: true,
          index: v.getSliceIndex(),
          total: v.getNumberOfSlices(),
          letters: axes ? orientationLetters(axes.right, axes.up) : null,
        });
        // The second axial is the same SLICE at another window: it follows
        // the axial's slice, and the axial follows it, so either can be
        // scrolled. Only the slice -- each keeps its own pan and zoom.
        // Copying the whole camera did that too, and froze the second
        // cell at whatever framing the axial had before the grid settled.
        const twin = p.key === "axial" ? second : p.key === "axial-second" ? 0 : -1;
        const other = twin >= 0 ? vp(twin) : undefined;
        const index = v.getSliceIndex();
        if (other && other.getSliceIndex() !== index) {
          void cs.core.utilities.jumpToSlice(other.element, { imageIndex: index });
        }
      };
      element.addEventListener(Events.VOI_MODIFIED, readVoi);
      element.addEventListener(Events.IMAGE_RENDERED, readVoi);
      element.addEventListener(Events.CAMERA_MODIFIED, readCamera);
      readVoi();
      readCamera();
      cleanups.push(() => {
        element.removeEventListener(Events.VOI_MODIFIED, readVoi);
        element.removeEventListener(Events.IMAGE_RENDERED, readVoi);
        element.removeEventListener(Events.CAMERA_MODIFIED, readCamera);
      });
    });
    return () => cleanups.forEach((c) => c());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, vp, patchPlane]);

  useEffect(() => {
    const element = hosts.current.find(Boolean);
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => engineRef.current?.resize(true, true));
    for (const h of hosts.current) if (h) observer.observe(h);
    return () => observer.disconnect();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      // The nav row and the arrow keys drive the AXIAL, which the others
      // follow through the crosshair; it is the plane the stack was read in.
      step: (delta) => {
        const v = vp(0);
        const cs = csRef.current;
        if (!v || !cs) return;
        const next = Math.max(0, Math.min(v.getNumberOfSlices() - 1, v.getSliceIndex() + delta));
        void cs.core.utilities.jumpToSlice(v.element, { imageIndex: next });
      },
      goTo: (i) => {
        const v = vp(0);
        const cs = csRef.current;
        if (!v || !cs) return;
        void cs.core.utilities.jumpToSlice(v.element, {
          imageIndex: Math.max(0, Math.min(v.getNumberOfSlices() - 1, i)),
        });
      },
      // ONE WINDOW ACROSS THE PLANES -- they are the same voxels -- except
      // the fourth cell, whose whole point is a different one.
      applyWindow: (width, centre) =>
        eachPlane((v, plane) => {
          if (!plane.window) v.setProperties({ voiRange: rangeOf({ width, centre }) });
        }),
      toggleInvert: () => {
        const next = !states[0]?.inverted;
        eachPlane((v) => v.setProperties({ invert: next }));
        setStates((prev) => prev.map((s) => ({ ...s, inverted: next })));
      },
      reset: () => {
        const cs = csRef.current;
        if (!cs) return;
        const { BlendModes } = cs.core.Enums;
        eachPlane((v, plane) => {
          v.resetProperties();
          v.resetCamera();
          if (plane.window) v.setProperties({ voiRange: rangeOf(plane.window) });
          if (view !== "mpr") {
            v.setBlendMode(view === "mip" ? BlendModes.MAXIMUM_INTENSITY_BLEND : BlendModes.MINIMUM_INTENSITY_BLEND);
            v.setSlabThickness(slab);
          }
        });
        setStates((prev) => prev.map((s) => ({ ...s, inverted: false })));
      },
      rotate: () => undefined,
      flip: () => undefined,
      plane: () => null,
      toCanvas: () => null,
    }),
    [vp, eachPlane, states, view, slab],
  );

  const modeText =
    view === "mpr" ? "MPR" : `${view === "mip" ? "MIP" : "MinIP"} · slab ${slab} mm`;

  return (
    <div className={`rr-grid rr-grid--${layout}`} data-cy="rr-volume">
      {planes.map((plane, i) => {
        if (!plane) {
          return (
            <div key={`none-${i}`} className="rr-cell rr-cell--empty" data-cy="rr-cell-empty">
              <p className="rr-empty-note">
                A second window is offered on CT only, where Hounsfield units make one meaningful.
              </p>
            </div>
          );
        }
        const s = states[i] ?? EMPTY_PLANE;
        const name = plane.window?.name ?? windowName(s.voi);
        return (
          <RoomCell
            key={plane.key}
            cy={`rr-plane-${plane.key}`}
            label={plane.label}
            colour={plane.colour}
            active={i === active}
            framed={planes.length > 1}
            overlays={overlays && s.ready}
            letters={s.letters}
            onActivate={() => setActive(i)}
            corners={{
              tl: (
                <>
                  <span className="rr-bright">{title}</span>
                  {titleLines.map((t) => (
                    <span key={t}>{t}</span>
                  ))}
                </>
              ),
              tr: s.total > 0 && (
                <span className="rr-bright" data-cy="rr-plane-position">
                  Im {s.index + 1}/{s.total}
                </span>
              ),
              bl: (
                <>
                  <span data-cy="rr-mode">{modeText}</span>
                  {s.voi && (
                    <span className="rr-bright">
                      W {s.voi.width} L {s.voi.centre}
                      {name ? ` · ${name}` : ""}
                    </span>
                  )}
                </>
              ),
            }}
          >
            <div
              ref={(el) => {
                hosts.current[i] = el;
              }}
              className="rr-canvas"
              onContextMenu={(e) => e.preventDefault()}
            />
          </RoomCell>
        );
      })}
      {!ready && (
        <p className="rr-loading rr-loading--over">Building the volume… {Math.round(progress * 100)}%</p>
      )}
    </div>
  );
});
